import { isPlatformBrowser } from "@angular/common";
import {
  Component,
  effect,
  EventEmitter,
  Inject,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  signal,
  SimpleChanges
} from '@angular/core';
import { Subject, Subscription } from "rxjs";
import { ModalService, PageService } from "@gea-waldviertler-website/shop-lib";
import { intersection } from "../../helpers";
import { CartService } from "../../services/cart.service";
import { ProductSelectorComponent } from "../product.selector.component";
import { SizeGuideComponent } from "../size-guide/size-guide.component";
import { ScreenSizeService } from "src/app/services/screen-size.service";
import { FormBuilder, FormGroup } from "@angular/forms";
import {
  AttributeSelectionsInterface,
  DistinctChildProductInterface,
  DistinctMatrixProductInterface,
  MatrixProduct,
  ProductAttributeInterface,
  ProductAttributeOptionInterface,
  ProductVariantInterface
} from "@gea-waldviertler-website/gea-models";

@Component({
    selector: 'gea-shop-configurator',
    templateUrl: './configurator.component.html',
    styleUrls: [],
    standalone: false
})
export class ConfiguratorComponent extends ProductSelectorComponent implements OnChanges, OnInit, OnDestroy {

  loadedProduct = input.required<MatrixProduct>()

  @Input() resetSelectionSubject?: Subject<boolean>
  // Re-emit trigger fired by ProductComponent on a same-product Aktion re-emit.
  @Input() distinctRefreshSubject?: Subject<void>
  @Input() triggerValidationSubject?: Subject<boolean>
  @Output("activeVariantChange") setVariantEvent = new EventEmitter<ProductVariantInterface>()
  @Output("distinctProductSelected") distinctProductSelectedEvent = new EventEmitter<DistinctMatrixProductInterface | undefined>()
  @Output("currentSelectionChange") currentSelectionChangeEvent = new EventEmitter<AttributeSelectionsInterface>()

  preselection = input<string[][]>([])

  screenService: ScreenSizeService

  currentVariant: ProductVariantInterface | undefined
  currentSelections: AttributeSelectionsInterface
  validating = signal<boolean>(false)
  distinctPrice: number | null
  platformId : Object

  form?: FormGroup
  fb: FormBuilder
  storePickupEnabled: boolean = true

  private subscriptions: Subscription[] = []

  constructor(
    fb: FormBuilder,
    @Inject(PLATFORM_ID) platformId: Object,
    @Inject(ModalService) private modalService: ModalService,
    @Inject(CartService) private cartService: CartService,
    @Inject(PageService) private pageService: PageService,
    @Inject(ScreenSizeService) screenSizeService: ScreenSizeService,
  ) {
    super()
    this.fb = fb
    this.currentSelections = {}
    this.currentVariant = undefined
    this.distinctPrice = null
    this.platformId = platformId
    this.screenService = screenSizeService

    this.pageService.getConfigSubject().subscribe(config => {
      if (config) this.storePickupEnabled = config.storePickupEnabled
    })

    effect(() => {
      const loadedProduct = this.loadedProduct()
      this.clearSelections()
      if (! loadedProduct) return

      // try to use url parameters in client mode only (no ssr)
      if (isPlatformBrowser(this.platformId)) {
        this.fixHiddenSelections(loadedProduct)
      }

      if (loadedProduct.variants.length == 1) {
        this.currentVariant = loadedProduct.getVariant()
        this.setVariantEvent.emit(this.currentVariant)

        this.handleDistinct()
      }
    })

    effect(() => {
      const ps = this.preselection()
      this.setPreselection(ps)
    })
  }

  ngOnInit() {
    const resetSub = this.resetSelectionSubject?.subscribe(value => {
      if (value) {
        this.clearSelections()
      }
    })
    if (resetSub) this.subscriptions.push(resetSub)

    const validationSub = this.triggerValidationSubject?.subscribe(value => {
      if (value) {
        this.validating.set(value)
      }
    })
    if (validationSub) this.subscriptions.push(validationSub)

    // Re-resolve + re-emit the distinct product on demand. On live, the active shop event ("Aktion")
    // arrives asynchronously after the initial render, so ProductService re-emits the product page for
    // the SAME instance. Re-running the resolution keeps an already-resolved matrix product's order
    // tiles + add-to-cart button alive and refreshes the Aktion price/badge.
    const refreshSub = this.distinctRefreshSubject?.subscribe(() => {
      this.handleDistinct()
    })
    if (refreshSub) this.subscriptions.push(refreshSub)

    this.form = this.fb.group({
      variablePrice: 0,
    })
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
    this.subscriptions = []
  }

  clearSelections() {
    this.currentSelections = {}
    this.currentVariant = undefined
    this.selectFirstSwatch()
  }

  showSizeGuide(): boolean {
    if (! this.loadedProduct().selectableAttributes) return false

    for(const code of this.loadedProduct().selectableAttributes.keys()) {
      if(code == 'leiste' || code == 'schuhgroesse') {
        return true
      }
    }
    return false
  }

  getTooltip(attribute: ProductAttributeInterface, option: ProductAttributeOptionInterface): string | null {
    if (! this.isSelectionPossible(attribute.code, option)) {
      return 'Die ' + attribute.title + ' ' + option.title + ' wird aktuell in der gewählten Kombination nicht angeboten.'
    }
    if (! this.isSelectionInStockOrReminder(attribute.code, option)) {
      return 'Die Auswahl ist aktuell nicht verfügbar.'
    }
    
    if (attribute.code == 'leiste') {
      return this.getShoeWidthTooltip(option.title)
    }

    let isSwatch = false
    for (var o of attribute.options) {
      if (o.imageSlug) {
        isSwatch = true
        break
      }
    }

    if (isSwatch) return option.title

    return null
  }

  getShoeWidthTooltip(option : string): string | null {
    switch(option) {
      //case '0': return "Kindergröße"
      //case '1': return "Kindergröße"
      case 'E': return "für sehr niedrige Fußrücken (Rist)"
      case 'F': return "für eher niedrige Fußrücken (Rist)"
      case 'G': return "für eher hohe Fußrücken (Rist)"
      case 'H': return "für sehr hohe Fußrücken (Rist)"
    }

    return null
  }

  toggleActiveSelection(attributeCode?: string,
    option?: ProductAttributeOptionInterface | null,
    throwEvent = true
    ): boolean {

    if (!attributeCode || !option) {
      return false
    }

    // do not toggle selection if not in stock or no reminder can be created
    if (! this.isSelectionInStockOrReminder(attributeCode, option)) {
      return false
    }

    // delete selection if clicked option has been already selected (deselect attribute selection)
    if (this.currentSelections.hasOwnProperty(attributeCode)
        && intersection(option.optionIds, this.currentSelections[attributeCode].attributeValues).length
        && this.loadedProduct().getSwatchAttribute()?.code != attributeCode) {
      delete this.currentSelections[attributeCode]

      // find out variant if any
      let selectedVariant = this.loadedProduct().getSelectedVariant(this.currentSelections)
      if (selectedVariant) {
        this.currentVariant = selectedVariant
      } else {
        this.currentVariant = undefined
      }
      this.setVariantEvent.emit(this.currentVariant)
    } else { // set attribute selection
      this.currentSelections[attributeCode] = {attributeCode: attributeCode, attributeValues: option.optionIds}

      // set pictures of variant, if variant has been changed
      let selectedVariant = this.loadedProduct().getSelectedVariant(this.currentSelections)
      if (selectedVariant && this.currentVariant !== selectedVariant) {
        this.currentVariant = selectedVariant
        this.setVariantEvent.emit(this.currentVariant)
      }
    }

    // check possible selections on other attributes and deselect if not possible
    Object.entries(this.currentSelections).reverse().forEach(([ac, values]) => {
      if (ac != attributeCode) {
        let option = this.loadedProduct().getSelectableOption(ac, values.attributeValues)
        if (option) {
          if (! this.isSelectionPossible(ac, option)
            || ! this.isSelectionInStockOrReminder(ac, option)) {
            delete this.currentSelections[ac]
          }
        }
      }
    })

    if (Object.entries(this.currentSelections).length == 0) {
      this.resetSelectionSubject?.next(true)
      return false
    }

    if (throwEvent) this.currentSelectionChangeEvent.emit(this.currentSelections)

    this.handleDistinct()

    return true
  }

  handleDistinct() {
    const distinctProduct = this.getDistinctProduct()
    
    const ds = this.getDistinctSelection()
    if (ds) {
      this.distinctPrice = this.getPrice(ds)
      this.distinctProductSelectedEvent.emit(distinctProduct)
      return
    }

    this.distinctPrice = null
    this.distinctProductSelectedEvent.emit(undefined)
  }

  /**
   * returns all distinct childs
   */
  getAllChildren(): DistinctChildProductInterface[] {
    let selections: DistinctChildProductInterface[] = []
    this.loadedProduct().variants.forEach(variant => {
      if (variant.children) {
        variant.children.forEach(attributeSelection => {
          selections.push(attributeSelection)
        })
      }
    })

    return selections
  }

  /**
   * returns distinct children by current selection
   *
   * @param excludeAttributeCode
   */
  getPossibleChildren(excludeAttributeCode?: string): DistinctChildProductInterface[]  {
    let allSelections = this.getAllChildren()
    let selections = Object.keys(this.currentSelections)

    // if no selection exists, return all
    if (selections.length === 0) {
      return allSelections
    }

    let possibleSelections: DistinctChildProductInterface[] = []

    for (let attributeSelection of allSelections) {
      let childAttributes = this.loadedProduct().getAllChildAttributes(attributeSelection.distinctId)
      let matchesAll = true

      Object.values(this.currentSelections).forEach(attribute => {
        if (childAttributes && excludeAttributeCode !== attribute.attributeCode) {
          matchesAll = matchesAll && attribute.attributeValues.includes(childAttributes[attribute.attributeCode])
        }
      })

      if (matchesAll) {
        possibleSelections.push(attributeSelection)
      }
    }

    return possibleSelections
  }

  openSizeInfo() {
    this.modalService.show(SizeGuideComponent, {
      title: "Größen- und Weiteninformationen",
      closeBtnText: undefined,
      modalOptions: {
        windowClass: 'size-guide-modal',
        size: 'lg',
      }
    })
  }

  hasAttributeOptionAction(attribute: ProductAttributeInterface, option: ProductAttributeOptionInterface) {
    return !! this.getAttributeOptionAction(attribute, option);
  }

  getAttributeOptionAction(
    attribute: ProductAttributeInterface, option: ProductAttributeOptionInterface
  ){
    let p: AttributeSelectionsInterface = {}
    p[attribute.code] = {
      attributeCode: attribute.code,
      attributeValues: option.optionIds,
    }

    const lp = this.loadedProduct()
    const variant = lp.getSelectedVariant(p)

    if (variant && lp.specialVariants) {
      for (const pvsa of Object.values(lp.specialVariants)) {
        if (pvsa.variants.map(v => {return v.variantId}).includes(variant.variantId)) {
          return pvsa.shopEvent
        }
      }
    }

    return undefined
  }

  isSelected(attributeCode?: string, option?: ProductAttributeOptionInterface): boolean {
    if (!attributeCode || !option) {
      return false
    }

    if (this.currentSelections.hasOwnProperty(attributeCode)) {
      if (intersection(option.optionIds, this.currentSelections[attributeCode].attributeValues).length) {
        return true
      }
    }

    return false
  }

  hasSelection(attributeCode?: string): boolean {
    if (! attributeCode) {
      return false
    }

    if (this.currentSelections.hasOwnProperty(attributeCode)) {
      return true
    }

    return false
  }

  isColorful(colorOption?: ProductAttributeOptionInterface): boolean {
    if(colorOption == undefined) {
      return true
    }

    return colorOption.title.toLocaleLowerCase() == "bunt"
  }

  isSelectionPossible(attributeCode?: string, option?: ProductAttributeOptionInterface): boolean | number {
    if (! attributeCode || ! option) {
      return false
    }

    let isPossible = false
    let pc = this.getPossibleChildren(attributeCode)

    if (! pc || pc.length == 0) {
      return false
    }

    for (let child of pc) {
      let childAttributes = this.loadedProduct().getAllChildAttributes(child.distinctId)
      if (childAttributes && option.optionIds.includes(childAttributes[attributeCode])) {
        isPossible = true
        break
      }
    }

    return isPossible
  }

  isSelectionInStockOrReminder(attributeCode?: string, option?: ProductAttributeOptionInterface): boolean {
    if (! attributeCode || ! option) {
      return false
    }

    const attribute = this.loadedProduct().selectableAttributes.get(attributeCode)
    if(! attribute) return false

    if(attribute.scope == 'V') {
      for (let i = 0; i < this.loadedProduct().variants.length; i++) {
        const variant = this.loadedProduct().variants[i]
        const optionId = variant.attributes.get(attributeCode)

        if(! optionId || ! option.optionIds.includes(optionId)) {
          continue
        }

        if(! variant.children)  {
          continue
        }

        for (let j = 0; j < variant.children.length; j++) {
          const child = variant.children[j]

          if(this.loadedProduct().canBeOrdered(this.storePickupEnabled, 1, variant.variantId, child.distinctId) || this.loadedProduct().isRemindPossible(variant)) {
            return true
          }
        }
      }

      return false
    }

    if(attribute.scope == 'U') {
      const variant = this.currentVariant
      if(! variant || ! variant.children) return false
      const possibleChildren = this.getPossibleChildren(attributeCode)

      for (let j = 0; j < variant.children.length; j++) {
        const child = variant.children[j]
        const optionId = child.map.get(attributeCode)

        if(! optionId || ! option.optionIds.includes(optionId)) {
          continue
        }
        if(possibleChildren.find(c => c.distinctId == child.distinctId) === undefined) {
          continue
        }
        if(this.loadedProduct().canBeOrdered(this.storePickupEnabled, 1, variant.variantId, child.distinctId) || this.loadedProduct().isRemindPossible(variant)) {
          return true
        }
      }
    }

    return false
  }

  hasDistinctSelection(): boolean {
    if (! this.loadedProduct()) return false
    if (! this.loadedProduct().selectableAttributes) return false

    if ([...this.loadedProduct().selectableAttributes.keys()].every(el => Object.keys(this.currentSelections).includes(el))) {
      return this.getPossibleChildren().length === 1
    }

    return false
  }

  getDistinctSelection(): DistinctChildProductInterface | null {
    if (this.hasDistinctSelection()) {
      return this.getPossibleChildren()[0]
    }

    return null
  }

  getDistinctProduct(): DistinctMatrixProductInterface | undefined {
    let distinctSelection = this.getDistinctSelection()

    if (distinctSelection == null) {
      return undefined
    }

    const price = this.getPrice(distinctSelection)
    if(price <= 0 || price > 10000) {
      return undefined
    }

    return {
      distinctId: distinctSelection.distinctId,
      product: this.loadedProduct(),
      selectedVariant: this.currentVariant ? this.currentVariant?.variantId : 0,
      price: price,
      priceSpecial: distinctSelection.priceSpecial ? distinctSelection.priceSpecial : undefined,
      qualityId: distinctSelection.qualityId
    }
  }

  changePrice() {
    const priceValue = this.form?.controls["variablePrice"].value
    if(priceValue && typeof priceValue === "number") {
      this.handleDistinct()
    }
  }

  getPrice(ds: DistinctChildProductInterface): number {
    if (this.currentVariant?.variablePrice) {
      return this.getVariablePrice() || 0
    } else if (ds.priceSpecial) {
      return ds.priceSpecial
    } else {
      return ds.price
    }
  }

  getVariablePrice() {
    if(!this.currentVariant?.variablePrice) return undefined

    const priceValue = this.form?.controls["variablePrice"].value
    if(priceValue && typeof priceValue === "number" && priceValue as number >= 1 && priceValue as number <= 10000) {
      return priceValue
    }

    return undefined
  }

  isValidPriceForm(): boolean {
    let priceValue = this.getVariablePrice()
    if(!priceValue || priceValue < 1) {
      return false
    }

    if (this.form?.valid === undefined) return true
    return this.form.valid
  }

  getOptionDisplayMode(attribute: ProductAttributeInterface): 'swatch' | 'text' {
    if(this.loadedProduct().getSwatchAttribute()?.code == attribute.code) {
      return 'swatch'
    } else {
      return 'text'
    }
  }

  isOptionVisible(option: ProductAttributeOptionInterface, attribute: ProductAttributeInterface, displayMode: 'swatch' | 'text') {
    if(displayMode == 'swatch') {
      return this.isSelectionInStockOrReminder(attribute.code, option) || attribute.options.length == 1
    } else if(displayMode == 'text') {
      return true
    }

    return false
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('triggerValidation')) {
      if (changes['triggerValidation'].currentValue) {
        this.validating.set(true)
      }
    }
  }

  // this method is called after stock has been loaded in product.component
  // TODO: this should be generalized
  private setPreselection(keyValues: string[][]) {
    if (keyValues.length) {
      // test if called product by cartitem in checkout
      if (keyValues[0][0] == 'cartItem') {
        const cartItem = this.cartService.get(keyValues[0][1])
        if (cartItem) {
          keyValues = []
          cartItem.getSelectedAttributeList().forEach((element, index) => {
            keyValues.push([ element.code, element.id + '' ])
          })
          this.preselectAttributes(keyValues)
        }
      } else {
        this.preselectAttributes(keyValues)
      }
    }
  }

  // this method selects attributes with one option value which is not visible
  private fixHiddenSelections(product: MatrixProduct) {
    for (const attribute of product.selectableAttributes.values()) {
      if (attribute.options.length == 1 && attribute.code != "leiste") {
        this.toggleActiveSelection(attribute.code, attribute.options[0], false)
      }
    }
  }

  private preselectAttributes(keyValues: string[][]) {
    // swatch attribute
    const swatchCode = this.loadedProduct().getSwatchAttribute()?.code
    let swatchAttributeSelected = false
    // remember the swatch option requested via the url hash
    let requestedSwatchOption: ProductAttributeOptionInterface | undefined

    for (let i = 0; i < keyValues.length; i++) {
      const code = keyValues[i][0]
      const options = this.loadedProduct().selectableAttributes.get(code)?.options
      if (options) {
        for (const option of options) {
          if (option.optionIds.includes(+keyValues[i][1])) {
            if (swatchCode == code) {
              requestedSwatchOption = option
            }
            // only toggle if this very attribute code is not already resolved to the requested option
            if (this.isSelected(code, option)) {
              if (swatchCode == code) swatchAttributeSelected = true
            } else {
              const selected = this.toggleActiveSelection(code, option, false)
              if (selected && swatchCode == code) {
                swatchAttributeSelected = true
              }
            }
          }
        }
      }
    }

    // if no swatch attribute has been selected, prefer the hash-requested swatch
    // before falling back to the first orderable swatch (options[0])
    if (! swatchAttributeSelected) {
      if (requestedSwatchOption && this.toggleActiveSelection(swatchCode, requestedSwatchOption, false)) {
        return
      }
      this.selectFirstSwatch()
    }
  }

  private selectFirstSwatch() {
    const attributeCode = this.loadedProduct().getSwatchAttribute()?.code
    if (attributeCode) {
      const options = this.loadedProduct().selectableAttributes.get(attributeCode)?.options
      if (! options || ! options.length) {
        return
      }

      // select first possible swatch
      for (const option of options) {
        if (this.toggleActiveSelection(attributeCode, option, false)) {
          return
        }
      }
    } else {
      for (const attribute of this.loadedProduct().selectableAttributes.values()) {
        if (! this.hasSelection(attribute.code)
          && this.getOptionDisplayMode(attribute) == "text"
          && attribute.scope == "V") {
          this.toggleActiveSelection(attribute.code, attribute.options[0], false)
          break
        }
      }
    }
  }

  isAttributeValid(attributeCode: string | undefined): boolean {
    return this.currentSelections.hasOwnProperty(attributeCode+'')
  }
}
